import { assign, fromCallback, fromPromise, setup } from 'xstate';
import type { AnyActorRef } from 'xstate';
import { firstValueFrom } from 'rxjs';

import type {
  TripLegConfidenceRequest,
  TripLegConfidenceResponse,
} from '@/data/requests/trip-leg-confidence';
import type {
  LocationConfidence,
  TripLegConfidence,
  TripLegConfidenceF1ValidatePartial,
  TripLegAddressConfidenceF1ValidatePartial,
} from '@/data/models/trip-leg-confidence.model';
import { TripLegType, type TripLeg } from '@/data/models/reservation.model';
import tripLegUtils from '@/services/triplegUtils';
import triplegService from '@/services/triplegService';

import type { TripArrayFieldContext } from './createTripArrayFieldMachine';
import type { TripTypeContext } from './tripType.machine';

export type TripLegAddressConfidenceInput = {
  pickupAddressRef: AnyActorRef;
  dropoffAddressRef: AnyActorRef;
  tripTypeRef: AnyActorRef;
};

export type TripLegAddressConfidencePayloadItem = {
  tripIndex: number;
  payload: TripLegConfidenceRequest;
  /** Whether the pickup side carried content when this payload was queued. */
  hasPickup: boolean;
  /** Whether the dropoff side carried content when this payload was queued. */
  hasDropoff: boolean;
};

export type TripLegConfidenceErrorPayload = {
  message?: string;
  errorCode?: string;
  errorCodeData?: Record<string, unknown>;
  params?: Record<string, unknown>;
};

/**
 * What we keep per trip index after a /score/trip-leg response lands. The flags
 * record which side(s) were sent in the request so the merge step knows which
 * pieces of the response to apply to the reservation payload.
 */
export type TripLegConfidenceStoredEntry = {
  response: TripLegConfidenceResponse;
  hasPickup: boolean;
  hasDropoff: boolean;
};

export type TripLegAddressConfidenceContext = {
  pickupAddressRef: AnyActorRef;
  dropoffAddressRef: AnyActorRef;
  tripTypeRef: AnyActorRef;
  /** Keys `${index}\0${pickupKey}\0${dropoffKey}` */
  validatedPairKeys: Record<string, true>;
  pendingConfidencePayloads: TripLegAddressConfidencePayloadItem[];
  confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry>;
  errorsByTripIndex: Record<number, TripLegConfidenceErrorPayload>;
  confidenceFetchBatch?: TripLegAddressConfidencePayloadItem[];
};

/**
 * Extracts the i18n translation key for a /score/trip-leg error response.
 * Backend returns codes like "03.07.256"; we look up `errorCodes.256` (last segment).
 */
export function tripLegConfidenceErrorTranslationKey(
  errorCode: string | undefined,
): string | null {
  if (!errorCode) return null;
  const lastPart = errorCode.split('.').pop();
  return lastPart ? `errorCodes.${lastPart}` : null;
}

type AddressParts = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateCode: string;
  zipCode: string;
};

const EMPTY_ADDRESS_PARTS: AddressParts = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateCode: '',
  zipCode: '',
};

/**
 * Parse Google Places `secondaryText` ("Tampa, FL 33610" / "Tampa, FL" /
 * "Tampa, FL, USA") into city / stateCode / zipCode. `explicitZip` (from the
 * Place's own `zipCode` field) wins over anything parsed from text.
 */
function parseSecondaryText(
  secondary: string | undefined,
  explicitZip: string,
): { city: string; stateCode: string; zipCode: string } {
  if (!secondary) {
    return { city: '', stateCode: '', zipCode: explicitZip };
  }
  const parts = secondary
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const city = parts[0] ?? '';
  const stateZipTokens = (parts[1] ?? '').split(/\s+/).filter(Boolean);
  return {
    city,
    stateCode: stateZipTokens[0] ?? '',
    zipCode: explicitZip || (stateZipTokens[1] ?? ''),
  };
}

/**
 * Normalize machine-stored address (string or Google Places-shaped object)
 * into the split fields the /score/trip-leg payload expects.
 */
function addressMachineValueToParts(val: unknown): AddressParts {
  if (val == null) return EMPTY_ADDRESS_PARTS;
  if (typeof val === 'string') {
    const line = val.trim();
    return line
      ? { ...EMPTY_ADDRESS_PARTS, addressLine1: line }
      : EMPTY_ADDRESS_PARTS;
  }
  if (typeof val !== 'object') return EMPTY_ADDRESS_PARTS;
  const p = val as {
    isHeader?: boolean;
    description?: string;
    mainText?: string;
    secondaryText?: string;
    zipCode?: string;
  };
  if (p.isHeader) return EMPTY_ADDRESS_PARTS;
  const addressLine1 = (p.mainText ?? p.description ?? '').trim();
  if (!addressLine1) return EMPTY_ADDRESS_PARTS;
  const explicitZip = (p.zipCode ?? '').trim();
  const { city, stateCode, zipCode } = parseSecondaryText(
    p.secondaryText,
    explicitZip,
  );
  return { addressLine1, addressLine2: '', city, stateCode, zipCode };
}

function hasAddressContent(parts: AddressParts): boolean {
  return parts.addressLine1.length > 0;
}

function addressPartsKey(parts: AddressParts): string {
  return `${parts.addressLine1}|${parts.city}|${parts.stateCode}|${parts.zipCode}`;
}

function readTripArrayValues(ref: AnyActorRef): unknown[] {
  const ctx = ref.getSnapshot().context as TripArrayFieldContext;
  return ctx.values.length ? ctx.values : [''];
}

function readTripLegType(ref: AnyActorRef): TripLegType {
  const ctx = ref.getSnapshot().context as TripTypeContext;
  return ctx.value;
}

function pairKey(
  index: number,
  pickup: AddressParts,
  dropoff: AddressParts,
): string {
  return `${index}\0${addressPartsKey(pickup)}\0${addressPartsKey(dropoff)}`;
}

function tripLegPartialFromAddressParts(
  pickup: AddressParts,
  dropoff: AddressParts,
): Partial<TripLeg> {
  return {
    pickUpAddress: { ...pickup },
    dropOffAddress: { ...dropoff },
  };
}

function buildPayloadItem(
  tripIndex: number,
  pickup: AddressParts,
  dropoff: AddressParts,
): TripLegAddressConfidencePayloadItem {
  return {
    tripIndex,
    payload: tripLegUtils.getTripLegConfidencePayload(
      tripLegPartialFromAddressParts(pickup, dropoff),
    ),
    hasPickup: hasAddressContent(pickup),
    hasDropoff: hasAddressContent(dropoff),
  };
}

function collectNewConfidencePayloads(
  context: TripLegAddressConfidenceContext,
): Pick<
  TripLegAddressConfidenceContext,
  'validatedPairKeys' | 'pendingConfidencePayloads'
> {
  const pickups = readTripArrayValues(context.pickupAddressRef);
  const dropoffs = readTripArrayValues(context.dropoffAddressRef);
  const len = Math.max(pickups.length, dropoffs.length, 1);
  const validatedPairKeys = { ...context.validatedPairKeys };
  const pendingConfidencePayloads: TripLegAddressConfidencePayloadItem[] = [];
  const isRoundTrip =
    readTripLegType(context.tripTypeRef) === TripLegType.RoundTrip;

  for (let i = 0; i < len; i++) {
    const pickup = addressMachineValueToParts(pickups[i]);
    const dropoff = addressMachineValueToParts(dropoffs[i]);
    // /score/trip-leg now accepts one-sided requests, so fire as soon as
    // either side has content. Skip only when both are empty.
    if (!hasAddressContent(pickup) && !hasAddressContent(dropoff)) {
      continue;
    }
    if (isRoundTrip) {
      const outboundTripIndex = 2 * i;
      const returnTripIndex = 2 * i + 1;
      const outboundKey = pairKey(outboundTripIndex, pickup, dropoff);
      if (!validatedPairKeys[outboundKey]) {
        validatedPairKeys[outboundKey] = true;
        pendingConfidencePayloads.push(
          buildPayloadItem(outboundTripIndex, pickup, dropoff),
        );
      }
      const returnKey = pairKey(returnTripIndex, dropoff, pickup);
      if (!validatedPairKeys[returnKey]) {
        validatedPairKeys[returnKey] = true;
        pendingConfidencePayloads.push(
          buildPayloadItem(returnTripIndex, dropoff, pickup),
        );
      }
    } else {
      const key = pairKey(i, pickup, dropoff);
      if (validatedPairKeys[key]) {
        continue;
      }
      validatedPairKeys[key] = true;
      pendingConfidencePayloads.push(buildPayloadItem(i, pickup, dropoff));
    }
  }

  return { validatedPairKeys, pendingConfidencePayloads };
}

const addressPairSubscription = fromCallback(
  ({
    sendBack,
    input,
  }: {
    sendBack: (event: { type: 'ADDRESSES_UPDATED' }) => void;
    input: TripLegAddressConfidenceInput;
  }) => {
    const emit = () => {
      sendBack({ type: 'ADDRESSES_UPDATED' });
    };
    const pickupSub = input.pickupAddressRef.subscribe(emit);
    const dropoffSub = input.dropoffAddressRef.subscribe(emit);
    const tripTypeSub = input.tripTypeRef.subscribe(emit);
    emit();
    return () => {
      pickupSub.unsubscribe();
      dropoffSub.unsubscribe();
      tripTypeSub.unsubscribe();
    };
  },
);

// TODO(F1V2-4815 reconciliation): /validate requires `tripLegConfidenceLogID` on
// the confidence block, but /score/trip-leg doesn't return one. Sending `0` as a
// placeholder until either /score/trip-leg starts returning a real log ID or
// /validate drops the requirement.
const MISSING_TRIP_LEG_CONFIDENCE_LOG_ID_SENTINEL = 0;

function tripLegConfidenceFromResponse(
  tlc: TripLegConfidence,
): TripLegConfidenceF1ValidatePartial {
  return {
    tripLegConfidenceLogID: MISSING_TRIP_LEG_CONFIDENCE_LOG_ID_SENTINEL,
    isTripLegConfident: tlc.isTripLegConfident,
    tripLegConfidenceReasonID: tlc.tripLegConfidenceReasonID,
    tripLegConfidenceReasonBC: tlc.tripLegConfidenceReasonBC,
  };
}

function addressConfidenceFromLocation(
  loc: LocationConfidence,
): TripLegAddressConfidenceF1ValidatePartial {
  const parsed = parseFloat(loc.confidenceScore);
  return {
    locationConfidenceLogID: loc.locationConfidenceLogID,
    locationStatusID: loc.locationStatusID,
    confidenceScore: Number.isNaN(parsed) ? null : parsed,
    confidenceDetails: loc.confidenceDetails,
    locationStatusBC: loc.locationStatusBC,
  };
}

/**
 * /score/trip-leg returns location confidence in two different shapes:
 *  - two-sided requests → nested under `tripLegConfidence` (`pickupLocationConfidence` / `dropOffLocationConfidence`),
 *  - one-sided requests → top-level on the response (`pickUpLocationConfidence` / `dropOffLocationConfidence`)
 *    with no `tripLegConfidence` wrapper.
 * The pickup field also has a casing inconsistency (`pickup…` nested vs `pickUp…` top-level).
 * These helpers normalize both shapes so the merge step doesn't care which one came back.
 */
function extractPickupLocationConfidence(
  response: TripLegConfidenceResponse,
): LocationConfidence | undefined {
  const top = response as unknown as {
    pickUpLocationConfidence?: LocationConfidence;
    pickupLocationConfidence?: LocationConfidence;
  };
  return (
    top.pickUpLocationConfidence ??
    top.pickupLocationConfidence ??
    response.tripLegConfidence?.pickupLocationConfidence
  );
}

function extractDropoffLocationConfidence(
  response: TripLegConfidenceResponse,
): LocationConfidence | undefined {
  const top = response as unknown as {
    dropOffLocationConfidence?: LocationConfidence;
  };
  return (
    top.dropOffLocationConfidence ??
    response.tripLegConfidence?.dropOffLocationConfidence
  );
}

/**
 * Merges stored trip-leg address confidence API responses into the reservation body for POST `/validate`.
 * `confidence` / `confidenceScore` are optional on the backend contract. Per-leg rules:
 *  - emit `pickUpAddress.confidenceScore` only when pickup was sent in the original /score/trip-leg request,
 *  - emit `dropOffAddress.confidenceScore` only when dropoff was sent,
 *  - emit leg-level `confidence` only when BOTH sides were sent (the trip-leg confidence is meaningless
 *    for a one-sided request).
 */
export function mergeTripLegConfidenceResponsesIntoReservationPayload(
  reservation: unknown,
  confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry>,
): unknown {
  if (
    typeof reservation !== 'object' ||
    reservation === null ||
    Object.keys(confidenceByTripIndex).length === 0
  ) {
    return reservation;
  }
  const r = reservation as { tripLegs?: unknown[] };
  if (!Array.isArray(r.tripLegs)) {
    return reservation;
  }
  return {
    ...r,
    tripLegs: r.tripLegs.map((leg, idx) => {
      const entry = confidenceByTripIndex[idx];
      if (!entry || typeof leg !== 'object' || leg === null) return leg;
      const { response, hasPickup, hasDropoff } = entry;
      const tlc = response.tripLegConfidence;
      const legObj = leg as Record<string, unknown>;
      const pickup = legObj.pickUpAddress as
        | Record<string, unknown>
        | undefined;
      const dropoff = legObj.dropOffAddress as
        | Record<string, unknown>
        | undefined;
      const pickupLoc = extractPickupLocationConfidence(response);
      const dropoffLoc = extractDropoffLocationConfidence(response);
      const emitLegConfidence = hasPickup && hasDropoff && tlc != null;
      const emitPickupScore = hasPickup && pickup != null && pickupLoc != null;
      const emitDropoffScore =
        hasDropoff && dropoff != null && dropoffLoc != null;
      return {
        ...legObj,
        ...(emitLegConfidence
          ? { confidence: tripLegConfidenceFromResponse(tlc) }
          : {}),
        ...(emitPickupScore
          ? {
              pickUpAddress: {
                ...pickup,
                confidenceScore: addressConfidenceFromLocation(pickupLoc),
              },
            }
          : {}),
        ...(emitDropoffScore
          ? {
              dropOffAddress: {
                ...dropoff,
                confidenceScore: addressConfidenceFromLocation(dropoffLoc),
              },
            }
          : {}),
      };
    }),
  };
}

export const tripLegAddressConfidenceMachine = setup({
  types: {
    context: {} as TripLegAddressConfidenceContext,
    events: {} as
      | { type: 'ADDRESSES_UPDATED' }
      | { type: 'RESET' }
      | { type: 'CLEAR_PENDING' },
    input: {} as TripLegAddressConfidenceInput,
  },
  actors: {
    addressPairSubscription,
    fetchConfidenceBatch: fromPromise<
      {
        responses: Record<number, TripLegConfidenceStoredEntry>;
        errors: Record<number, TripLegConfidenceErrorPayload>;
      },
      { items: TripLegAddressConfidencePayloadItem[] }
    >(async ({ input }) => {
      const responses: Record<number, TripLegConfidenceStoredEntry> = {};
      const errors: Record<number, TripLegConfidenceErrorPayload> = {};
      await Promise.all(
        input.items.map(
          async ({ tripIndex, payload, hasPickup, hasDropoff }) => {
            try {
              const response = await firstValueFrom(
                triplegService.validateTripLegAddress(payload),
              );
              responses[tripIndex] = { response, hasPickup, hasDropoff };
            } catch (err: unknown) {
              // Capture the error payload (e.g. confidence-below-threshold)
              // so the form can surface it as a dropoff field error.
              // `httpService.resolveRequest` rejects with the response body
              // (`{ message, errorCode, errorCodeData, ... }`).
              errors[tripIndex] =
                err && typeof err === 'object'
                  ? (err as TripLegConfidenceErrorPayload)
                  : { message: String(err) };
            }
          },
        ),
      );
      return { responses, errors };
    }),
  },
}).createMachine({
  id: 'tripLegAddressConfidence',
  context: ({ input }) => ({
    pickupAddressRef: input.pickupAddressRef,
    dropoffAddressRef: input.dropoffAddressRef,
    tripTypeRef: input.tripTypeRef,
    validatedPairKeys: {},
    pendingConfidencePayloads: [],
    confidenceByTripIndex: {},
    errorsByTripIndex: {},
  }),
  initial: 'running',
  states: {
    running: {
      initial: 'synced',
      invoke: {
        id: 'addressPairSubscription',
        src: 'addressPairSubscription',
        input: ({ context }) => ({
          pickupAddressRef: context.pickupAddressRef,
          dropoffAddressRef: context.dropoffAddressRef,
          tripTypeRef: context.tripTypeRef,
        }),
      },
      states: {
        synced: {
          on: {
            ADDRESSES_UPDATED: {
              actions: assign(({ context }) => {
                const next = collectNewConfidencePayloads(context);
                return {
                  validatedPairKeys: next.validatedPairKeys,
                  pendingConfidencePayloads: [
                    ...context.pendingConfidencePayloads,
                    ...next.pendingConfidencePayloads,
                  ],
                };
              }),
            },
            CLEAR_PENDING: {
              actions: assign({ pendingConfidencePayloads: () => [] }),
            },
            RESET: {
              actions: assign({
                validatedPairKeys: () => ({}),
                pendingConfidencePayloads: () => [],
                confidenceByTripIndex: () => ({}),
                errorsByTripIndex: () => ({}),
                confidenceFetchBatch: undefined,
              }),
            },
          },
          always: [
            {
              guard: ({ context }) =>
                context.pendingConfidencePayloads.length > 0,
              target: 'fetching',
            },
          ],
        },
        fetching: {
          entry: assign(({ context }) => ({
            confidenceFetchBatch: [...context.pendingConfidencePayloads],
            pendingConfidencePayloads: [],
          })),
          invoke: {
            id: 'fetchConfidenceBatch',
            src: 'fetchConfidenceBatch',
            input: ({ context }) => ({
              items: context.confidenceFetchBatch ?? [],
            }),
            onDone: {
              target: 'synced',
              actions: assign(({ context, event }) => {
                const { responses, errors } = event.output;
                // Drop stale errors for any leg that produced a fresh response,
                // and stale responses for any leg that produced a fresh error.
                const fetchedIndices = new Set<number>(
                  (context.confidenceFetchBatch ?? []).map((p) => p.tripIndex),
                );
                const nextResponses: Record<
                  number,
                  TripLegConfidenceStoredEntry
                > = {};
                const nextErrors: Record<
                  number,
                  TripLegConfidenceErrorPayload
                > = {};
                for (const [k, v] of Object.entries(
                  context.confidenceByTripIndex,
                )) {
                  const idx = Number(k);
                  if (!fetchedIndices.has(idx)) nextResponses[idx] = v;
                }
                for (const [k, v] of Object.entries(
                  context.errorsByTripIndex,
                )) {
                  const idx = Number(k);
                  if (!fetchedIndices.has(idx)) nextErrors[idx] = v;
                }
                return {
                  confidenceByTripIndex: { ...nextResponses, ...responses },
                  errorsByTripIndex: { ...nextErrors, ...errors },
                  confidenceFetchBatch: undefined,
                };
              }),
            },
            onError: {
              target: 'synced',
              actions: assign({
                confidenceFetchBatch: undefined,
              }),
            },
          },
          on: {
            ADDRESSES_UPDATED: {
              actions: assign(({ context }) => {
                const next = collectNewConfidencePayloads(context);
                return {
                  validatedPairKeys: next.validatedPairKeys,
                  pendingConfidencePayloads: [
                    ...context.pendingConfidencePayloads,
                    ...next.pendingConfidencePayloads,
                  ],
                };
              }),
            },
            CLEAR_PENDING: {
              actions: assign({ pendingConfidencePayloads: () => [] }),
            },
            RESET: {
              target: 'synced',
              actions: assign({
                validatedPairKeys: () => ({}),
                pendingConfidencePayloads: () => [],
                confidenceByTripIndex: () => ({}),
                errorsByTripIndex: () => ({}),
                confidenceFetchBatch: undefined,
              }),
            },
          },
        },
      },
    },
  },
});
